// Natural Grip Effect
// Narrative: High-quality, 3D-feeling roots and thorny vines rapidly burst from the earth,
// violently entangle the target area, constrict, and viciously drag the entire target down into the ground.

const CONFIG = {
    vineCount: 22,
    shakeIntensity: 20,
    shakeDuration: 1600,
    totalDuration: 7000
};

type VineNode = {
    angleOffset: number;
    radiusOffset: number;
    thickness: number;
    hasThorn: boolean;
    hasLeaf: boolean;
    leafAngle: number;
    leafSize: number;
};

class Vine3D {
    nodes: VineNode[] = [];
    maxNodes: number;
    baseAngle: number;
    baseRadius: number;
    twistRate: number;
    vSpacing: number;
    colorHue: number;
    colorLightness: number;
    delay: number;

    constructor(delay: number) {
        this.delay = delay;
        this.maxNodes = 35 + Math.floor(Math.random() * 20);
        this.baseAngle = Math.random() * Math.PI * 2;
        this.baseRadius = 50 + Math.random() * 40; // wrap around avatar
        this.twistRate = (Math.random() < 0.5 ? 1 : -1) * (0.05 + Math.random() * 0.1);
        this.vSpacing = 4 + Math.random() * 5;

        // Colors: Browns, Dark Greens, Swamp Greens
        const isWood = Math.random() < 0.3;
        if (isWood) {
            this.colorHue = 20 + Math.random() * 20; // brown
            this.colorLightness = 15 + Math.random() * 15;
        } else {
            this.colorHue = 80 + Math.random() * 60; // green to rich green
            this.colorLightness = 20 + Math.random() * 25;
        }

        let baseThickness = 8 + Math.random() * 14;

        for (let i = 0; i < this.maxNodes; i++) {
            // Taper thickness
            let t = baseThickness;
            if (i > this.maxNodes - 10) t = baseThickness * ((this.maxNodes - i) / 10);

            // Random chance for thorn or leaf
            let thorn = i > 2 && i < this.maxNodes - 2 && Math.random() < 0.15;
            let leaf = i > 4 && Math.random() < 0.2;

            this.nodes.push({
                angleOffset: (Math.random() - 0.5) * 0.5,
                radiusOffset: (Math.random() - 0.5) * 10,
                thickness: Math.max(1, t),
                hasThorn: thorn,
                hasLeaf: leaf,
                leafAngle: Math.random() * Math.PI * 2,
                leafSize: 15 + Math.random() * 20
            });
        }
    }
}

class DirtParticle {
    x: number; y: number; vx: number; vy: number;
    life: number; size: number; color: string; rotation: number; rotSpeed: number;
    isWood: boolean;

    constructor(x: number, y: number, burstForce: number = 1.0) {
        this.x = x + (Math.random() - 0.5) * 120;
        this.y = y + (Math.random() - 0.5) * 40 + 50; // start slightly below base
        this.vx = (Math.random() - 0.5) * 10 * burstForce;
        this.vy = -(2.5 + Math.random() * 12.5) * burstForce;
        this.life = 1.0;
        this.size = 3 + Math.random() * 12;
        this.rotation = Math.random() * Math.PI * 2;
        this.rotSpeed = (Math.random() - 0.5) * 0.25;
        this.isWood = Math.random() < 0.2; // 20% splinters

        const isGreen = Math.random() > 0.8; // 20% leaves/magic
        if (isGreen) {
            this.color = `rgba(${50 + Math.random() * 50}, ${150 + Math.random() * 100}, ${50 + Math.random() * 50}, 1)`;
            this.vy *= 0.5; // magic floats
        } else if (this.isWood) {
            const v = 50 + Math.random() * 40;
            this.color = `rgba(${v}, ${v - 30}, ${v - 40}, 1)`; // splinters
        } else {
            const v = 20 + Math.random() * 30;
            this.color = `rgba(${v + 10}, ${v}, ${v - 10}, 1)`; // dirt
        }
    }

    update() {
        this.vy += 0.6; // gravity
        this.x += this.vx;
        this.y += this.vy;
        this.rotation += this.rotSpeed;
        this.life -= 0.0075;
    }

    draw(ctx: CanvasRenderingContext2D) {
        if (this.life <= 0) return;
        ctx.save();
        ctx.translate(this.x, this.y);
        ctx.rotate(this.rotation);
        ctx.globalAlpha = Math.max(0, this.life);
        ctx.fillStyle = this.color;
        if (this.isWood) {
            // Draw splinter
            ctx.fillRect(-this.size / 2, -this.size * 2, this.size, this.size * 4);
        } else {
            // Draw chunk
            ctx.beginPath();
            ctx.arc(0, 0, this.size, 0, Math.PI * 2);
            ctx.fill();
        }
        ctx.restore();
    }
}

export type NaturalGripEffect = {
    kind: 'naturalGrip'
    t: number
    tx: number
    ty: number
    data?: {
        vines: Vine3D[];
        particles: DirtParticle[];
        phase: 'ERUPTION' | 'CONSTRICTION' | 'DRAG_DOWN' | 'BURIED';
        dragOffset: number;
        shakeOffsetX: number;
        shakeOffsetY: number;
        targetRadiusMult: number;
        globalTwist: number;
    }
}

export const createNaturalGripEffect = (tx: number, ty: number): NaturalGripEffect => ({
    kind: 'naturalGrip',
    t: 0,
    tx,
    ty
})

export const renderNaturalGripEffect = (
    eff: NaturalGripEffect,
    ctx: CanvasRenderingContext2D
): boolean => {
    if (eff.t > CONFIG.totalDuration) return false;

    if (!eff.data) {
        eff.data = {
            vines: [],
            particles: [],
            phase: 'ERUPTION',
            dragOffset: 0,
            shakeOffsetX: 0,
            shakeOffsetY: 0,
            targetRadiusMult: 1.0,
            globalTwist: 0
        };

        // Init vines with staggered delays
        for (let i = 0; i < CONFIG.vineCount; i++) {
            eff.data.vines.push(new Vine3D(Math.random() * 600));
        }

        // Initial dirt poof
        for (let i = 0; i < 20; i++) eff.data.particles.push(new DirtParticle(eff.tx, eff.ty, 0.5));
    }

    const d = eff.data;

    // State machine
    if (d.phase === 'ERUPTION' && eff.t > 1600) {
        d.phase = 'CONSTRICTION';
    } else if (d.phase === 'CONSTRICTION' && eff.t > 3000) {
        d.phase = 'DRAG_DOWN';
        // Massive dirt explosion!
        for (let i = 0; i < 60; i++) d.particles.push(new DirtParticle(eff.tx, eff.ty, 2.0));
    } else if (d.phase === 'DRAG_DOWN' && eff.t > 4800) {
        d.phase = 'BURIED';
    }

    // Logic updates
    d.particles.forEach(p => p.update());

    // Physics
    let isDragging = d.phase === 'DRAG_DOWN';

    if (d.phase === 'CONSTRICTION') {
        // Tighten radius and wiggle slightly
        d.targetRadiusMult = Math.max(0.4, d.targetRadiusMult - 0.025);
        d.shakeOffsetX = (Math.random() - 0.5) * 4;
        d.shakeOffsetY = (Math.random() - 0.5) * 4;
        d.globalTwist += 0.01; // wring the target
    } else if (isDragging) {
        // Violent drag down
        d.dragOffset += 17.5; // Accelerate into the earth rapidly
        d.globalTwist += 0.04;

        // Massive Screen shake
        d.shakeOffsetX = (Math.random() - 0.5) * CONFIG.shakeIntensity;
        d.shakeOffsetY = (Math.random() - 0.5) * CONFIG.shakeIntensity;
    } else {
        d.shakeOffsetX *= 0.5;
        d.shakeOffsetY *= 0.5;
        d.globalTwist += 0.0025; // gentle idle twisting
    }

    // ----------------------------------------------------
    // BUILD 3D SEGMENTS
    // ----------------------------------------------------
    type Segment3D = {
        x1: number, y1: number, z1: number,
        x2: number, y2: number, z2: number,
        zCenter: number,
        thickness: number,
        hue: number, lightness: number,
        hasThorn: boolean, hasLeaf: boolean,
        leafAngle: number, leafSize: number,
        angle1: number, angle2: number, // for lighting normal
        angleCenter: number // for lighting
    };

    let allSegments: Segment3D[] = [];

    d.vines.forEach(vine => {
        if (eff.t < vine.delay) return;

        let age = eff.t - vine.delay;

        // We add nodes over time to simulate growth.
        // During ERUPTION it grows 1 node per ~24ms.
        // During DRAG_DOWN, we stop growing and just pull everything down via dragOffset.
        let activeNodes = Math.min(vine.maxNodes, Math.floor(age / 24));
        if (d.phase === 'DRAG_DOWN' || d.phase === 'BURIED') {
            activeNodes = vine.maxNodes; // keep them fully grown
        }

        let px: number = 0, py: number = 0, pz: number = 0, pAngle: number = 0;

        for (let i = 0; i < activeNodes; i++) {
            let node = vine.nodes[i];

            // Taper radius slowly to form a cone at the bottom
            let rMult = 1.0;
            if (i < 8) rMult = i / 8;

            let r = (vine.baseRadius * d.targetRadiusMult * rMult) + node.radiusOffset;
            let angle = vine.baseAngle + (i * vine.twistRate) + d.globalTwist + node.angleOffset;

            let x = eff.tx + Math.cos(angle) * r;
            let z = Math.sin(angle) * r;
            // Base Y starts below ground (eff.ty + 200) and stacks upwards
            // The sine wave adds some slither/wriggle to the upward stretch
            let ySlither = Math.sin(eff.t * 0.0025 + i * 0.3) * 5;
            let y = eff.ty + 180 - (i * vine.vSpacing) + ySlither + d.dragOffset;

            if (i > 0) {
                allSegments.push({
                    x1: px, y1: py, z1: pz,
                    x2: x, y2: y, z2: z,
                    zCenter: (pz + z) / 2,
                    thickness: node.thickness,
                    hue: vine.colorHue,
                    lightness: vine.colorLightness,
                    hasThorn: node.hasThorn,
                    hasLeaf: node.hasLeaf,
                    leafAngle: node.leafAngle,
                    leafSize: node.leafSize,
                    angle1: pAngle,
                    angle2: angle,
                    angleCenter: (pAngle + angle) / 2
                });
            }
            px = x; py = y; pz = z; pAngle = angle;
        }
    });

    // Depth Sorting! Back to front (Negative Z is far away/behind target, Positive Z is in front)
    allSegments.sort((a, b) => a.zCenter - b.zCenter);

    // ----------------------------------------------------
    // RENDERING
    // ----------------------------------------------------
    ctx.save();

    // Apply Shake
    if (Math.abs(d.shakeOffsetX) > 0.5) {
        ctx.translate(d.shakeOffsetX, d.shakeOffsetY);
    }

    // 1. Dark Magic Hole / Ground Aura
    let auraAlpha = 0;
    if (eff.t < 800) auraAlpha = eff.t / 800 * 0.8;
    else if (eff.t > 5600) auraAlpha = 0.8 - ((eff.t - 5600) / 1400) * 0.8;
    else auraAlpha = 0.8;

    if (auraAlpha > 0.01) {
        // Draw the dark pit at the target's feet
        ctx.save();
        ctx.translate(eff.tx, eff.ty + 100); // 100px below center (feet area)
        ctx.scale(2.0, 0.6); // Flat ellipse
        ctx.fillStyle = `rgba(5, 15, 5, ${auraAlpha})`;
        const grad = ctx.createRadialGradient(0, 0, 0, 0, 0, 100);
        grad.addColorStop(0, `rgba(5, 25, 5, ${auraAlpha})`);
        grad.addColorStop(0.3, `rgba(0, 10, 0, ${auraAlpha * 0.8})`);
        grad.addColorStop(1, 'rgba(0,0,0,0)');
        ctx.fillStyle = grad;
        ctx.beginPath();
        ctx.arc(0, 0, 100, 0, Math.PI * 2);
        ctx.fill();
        ctx.restore();
    }

    // 2. Render Procedural 3D Vines
    // We render them using overlapping strokes. Round line caps create perfect smooth 3D-looking tubes!
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';

    allSegments.forEach(seg => {
        // Optimization: Only render if reasonably on screen (skip deeply buried ones)
        if (seg.y1 > eff.ty + 400 && seg.y2 > eff.ty + 400) return;

        // Base shadow/dark edge
        ctx.lineWidth = seg.thickness;
        ctx.strokeStyle = `hsl(${seg.hue}, 40%, ${seg.lightness - 10}%)`;
        ctx.beginPath();
        ctx.moveTo(seg.x1, seg.y1);
        ctx.lineTo(seg.x2, seg.y2);
        ctx.stroke();

        // Core highlight / 3D rim lighting
        // The highlight is slightly offset to simulate the light source coming from top-left
        ctx.lineWidth = seg.thickness * 0.5;

        // If the segment is facing the light (negative sine), make it brighter
        let lightVal = Math.sin(seg.angleCenter) > 0 ? -5 : 10;
        ctx.strokeStyle = `hsl(${seg.hue}, 50%, ${seg.lightness + lightVal}%)`;
        ctx.beginPath();
        ctx.moveTo(seg.x1 - 1, seg.y1 - 1);
        ctx.lineTo(seg.x2 - 1, seg.y2 - 1);
        ctx.stroke();

        // Thorns
        if (seg.hasThorn) {
            // Draw thorn pointing outward from center X
            let toCenterX = seg.x2 - eff.tx;
            let dirX = toCenterX > 0 ? 1 : -1;

            // Randomly tilt thorn up or down slightly relative to direction
            let thornLen = seg.thickness * 1.5 + 5;
            let tx = seg.x2 + dirX * thornLen;
            let ty = seg.y2 + (Math.random() - 0.5) * thornLen;

            ctx.fillStyle = '#2d251d'; // dark sharp wood
            ctx.beginPath();
            ctx.moveTo(seg.x2, seg.y2 - seg.thickness * 0.4); // base top
            ctx.lineTo(tx, ty); // point
            ctx.lineTo(seg.x2, seg.y2 + seg.thickness * 0.4); // base bottom
            ctx.fill();

            // Thorn highlight
            ctx.strokeStyle = '#4a3f35';
            ctx.lineWidth = 1;
            ctx.beginPath();
            ctx.moveTo(seg.x2, seg.y2);
            ctx.lineTo(tx, ty);
            ctx.stroke();
        }

        // Leaves
        if (seg.hasLeaf) {
            ctx.save();
            ctx.translate(seg.x2, seg.y2);
            ctx.rotate(seg.leafAngle + d.globalTwist * 2); // flutter with twist

            // Base leaf
            ctx.fillStyle = `hsl(${seg.hue + 15}, 65%, ${seg.lightness + 10}%)`;
            ctx.beginPath();
            ctx.moveTo(0, 0);
            ctx.quadraticCurveTo(seg.leafSize * 0.5, -seg.leafSize * 0.6, seg.leafSize, 0); // top edge
            ctx.quadraticCurveTo(seg.leafSize * 0.5, seg.leafSize * 0.6, 0, 0); // bottom edge
            ctx.fill();

            // Leaf vein (dark)
            ctx.strokeStyle = `hsl(${seg.hue + 15}, 60%, ${seg.lightness - 15}%)`;
            ctx.lineWidth = seg.thickness > 10 ? 2 : 1;
            ctx.beginPath();
            ctx.moveTo(0, 0);
            ctx.lineTo(seg.leafSize * 0.8, 0); // center vein
            ctx.stroke();
            ctx.restore();
        }
    });

    // 3. Dirt Particles (Foreground splash)
    ctx.globalCompositeOperation = 'source-over';
    d.particles.forEach(p => p.draw(ctx));

    ctx.restore();
    return true;
}
